import { SyntheticEvent, useEffect } from "react";

import { useTranslator } from "@eo-locale/react";

import filter from "lodash/filter";

import transform from "lodash/transform";

import {
  Box,
  Button,
  VStack,
  Text,
  Alert,
  Spinner,
  Center
} from "@/storybook-components";
import {
  validateQuestion,
  reduceQuestionsToFlattenedTrees
} from "@/helpers/questions";
import {
  fetchSignupFormResponses,
  updateSignupFormResponses
} from "@/redux/features/signup-form-responses/thunk";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import RequiredField from "@/components/questions/required-field";
import QuestionFactory from "@/components/questions/question-factory";
import type { Question } from "@/types";
import {
  resetAnswers,
  setAllAnswers,
  setError,
  setStatus
} from "@/redux/features/questions/slice";
import { highlightError } from "@/helpers/highlight-error";
import { fetchSignupFormQuestions } from "@/redux/features/signup-form-questions/thunk";
import { setHasPersonalInfoChanged } from "@/redux/features/signup-form-responses/slice";

const EXCLUDED_QUESTION_TYPES = [
  "SignupForm::LoginQuestion",
  "SignupForm::AccountEmailQuestion"
];

interface ProfileGateFormProps {
  reason: string;
  onComplete: () => void;
  onDismissOptional?: () => void;
}

const ProfileGateForm = ({
  reason,
  onComplete,
  onDismissOptional
}: ProfileGateFormProps) => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const profileGateMode = useAppSelector((s) => s.authGate.profileGateMode);
  const showOptionalSkip =
    profileGateMode === "optional" && typeof onDismissOptional === "function";
  const { answers, status } = useAppSelector((state) => state.questions);
  const { signupFormQuestions, status: questionsLoadStatus } = useAppSelector(
    (state) => state.signupFormQuestions
  );
  const {
    error: responsesError,
    signupFormResponses,
    status: responsesStatus
  } = useAppSelector((state) => state.signupFormResponses);

  const isLoading =
    questionsLoadStatus === "fetching" || responsesStatus === "fetching";
  const isSaving = status === "saving" || responsesStatus === "saving";

  useEffect(() => {
    if (!signupFormQuestions.length) dispatch(fetchSignupFormQuestions({}));
    dispatch(fetchSignupFormResponses());
  }, [dispatch, signupFormQuestions.length]);

  useEffect(() => {
    dispatch(resetAnswers());
    if (Object.keys(signupFormResponses).length) {
      dispatch(
        setAllAnswers(
          transform(
            signupFormResponses,
            function (result, value, key) {
              // @ts-ignore
              return (result[key] = value?.value || value?.id || "");
            },
            {}
          )
        )
      );
    }
  }, [dispatch, signupFormResponses]);

  const allQuestions = reduceQuestionsToFlattenedTrees(
    signupFormQuestions,
    answers
  );

  const editableQuestions = filter(
    allQuestions,
    ({ type }) => !EXCLUDED_QUESTION_TYPES.includes(type)
  );

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    dispatch(setStatus("saving"));
    let isError = false;

    for (let i = 0; i < editableQuestions.length; i++) {
      const question = editableQuestions[i];
      const error = await validateQuestion(answers[question.id], question);

      if (error?.length) {
        isError = true;
        dispatch(setStatus("idle"));
        dispatch(setError({ questionId: question.id, error }));
      }
    }

    if (document.querySelector(".ehqQuestionError")) {
      dispatch(setStatus("idle"));
      return highlightError();
    }

    if (!isError) {
      await dispatch(updateSignupFormResponses());
      dispatch(setHasPersonalInfoChanged(false));
      dispatch(setStatus("idle"));
      onComplete();
    }
  };

  if (isLoading) {
    return (
      <Center pb={4}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <VStack gap={0} align="stretch">
      <Text>{translate(reason || "profileGate.youAreAlmostDone")}</Text>

      <Box as="form" noValidate onSubmit={handleSubmit}>
        <RequiredField />

        {editableQuestions.map((question: Question) => (
          <QuestionFactory key={question.id} question={question} />
        ))}

        {responsesError === "Rejected" && (
          <Alert.Root status="error" borderRadius="md" my={2}>
            <Alert.Indicator />
            <Alert.Title>{translate("common.reviewChanges")}</Alert.Title>
          </Alert.Root>
        )}

        <Box
          position="sticky"
          bottom={0}
          zIndex={1}
          pt={6}
          pb={4}
          mt={2}
          bg="white"
        >
          <VStack gap={3} width="100%">
            <Button
              width="100%"
              variant="primary"
              type="submit"
              loading={isSaving}
            >
              {translate("profileGate.saveAndContinue")}
            </Button>

            {showOptionalSkip && (
              <Button
                width="100%"
                variant="link"
                type="button"
                onClick={onDismissOptional}
                disabled={isSaving}
              >
                {translate("profileGate.skipForNow")}
              </Button>
            )}
          </VStack>
        </Box>
      </Box>
    </VStack>
  );
};

export default ProfileGateForm;
